import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import soundpillLogo from '../../assets/images/eunpyeongcare/soundpill-logo.png';
import {
  CouponDashboardError,
  EunpyeongCareCoupon,
  fetchEunpyeongCareCoupon,
  localizedCouponText,
} from '../../services/eunpyeongCareDashboard.service';
import './eunpyeongcare-dashboard.css';

const REFRESH_LIMIT = 5;
const REFRESH_COOLDOWN_MS = 60_000;
const ORG_NAME = '불광보건지소';

const count = (value: number) => value.toLocaleString('ko-KR');
const dateFormat = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});
const timeFormat = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
});
const date = (value: string | number) => dateFormat.format(new Date(value));
const time = (value: string | number) => timeFormat.format(new Date(value));

type StatusTone = 'active' | 'expired' | 'exhausted';

function couponStatus(coupon: EunpyeongCareCoupon): {
  tone: StatusTone;
  label: string;
  description: string;
} {
  if (coupon.isExpired || (coupon.expiresAt && new Date(coupon.expiresAt).getTime() <= Date.now())) {
    return {
      tone: 'expired',
      label: '등록 기간 만료',
      description: '남은 수량이 있어도 등록 만료일이 지난 쿠폰은 등록할 수 없습니다.',
    };
  }
  if (!coupon.isUnlimited && coupon.remainingUses === 0) {
    return {
      tone: 'exhausted',
      label: '등록 수량 소진',
      description: '전체 누적 등록 건수가 최대 등록 수량에 도달했습니다.',
    };
  }
  return {
    tone: 'active',
    label: '등록 진행 중',
    description: '',
  };
}

function couponBenefit(coupon: EunpyeongCareCoupon) {
  if (coupon.type === 'discount') return `할인 값 ${count(coupon.value)}`;
  const units = { day: '일', month: '개월', year: '년' };
  return `${count(coupon.value)}${coupon.timeUnit ? units[coupon.timeUnit] : ''} 구독권`;
}

function resolveRate(coupon: EunpyeongCareCoupon) {
  if (coupon.isUnlimited || coupon.maxUses <= 0) return null;
  const ratio = Math.min(100, Math.max(0, (coupon.uses / coupon.maxUses) * 100));
  const label = ratio.toLocaleString('ko-KR', { maximumFractionDigits: 1 });
  return { ratio, label: `${label}%` };
}

function RefreshIcon({ spinning }: { spinning: boolean }) {
  return (
    <svg
      className={spinning ? 'epd-spin' : undefined}
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M20 7v5h-5M4 17v-5h5" />
      <path d="M6.1 6.1A8 8 0 0 1 20 12M4 12a8 8 0 0 0 13.9 5.9" />
    </svg>
  );
}

function CouponContent({ coupon }: { coupon: EunpyeongCareCoupon }) {
  const status = couponStatus(coupon);
  const rate = resolveRate(coupon);
  const name = localizedCouponText(coupon.name, '은평케어 쿠폰');
  const description = localizedCouponText(coupon.description);

  return (
    <>
      {status.tone !== 'active' ? (
        <div className={`epd-notice epd-notice--${status.tone}`} role="status">
          <strong>{status.label}</strong>
          {status.description ? <p>{status.description}</p> : null}
        </div>
      ) : null}

      <section className="epd-panel" aria-labelledby="epd-overview-title">
        <h2 id="epd-overview-title" className="epd-panel__title">
          쿠폰 등록 현황
        </h2>

        <div className="epd-metrics">
          <div className="epd-metric epd-metric--primary">
            <p className="epd-metric__label">누적 등록 건수</p>
            <p className="epd-metric__value">
              <span className="epd-metric__num">{count(coupon.uses)}</span>
              <span className="epd-metric__unit">건</span>
            </p>
          </div>
          <div className="epd-metric">
            <p className="epd-metric__label">남은 등록 수량</p>
            <p className="epd-metric__value">
              {coupon.isUnlimited ? (
                <span className="epd-metric__num epd-metric__num--secondary">무제한</span>
              ) : (
                <>
                  <span className="epd-metric__num epd-metric__num--secondary">
                    {count(coupon.remainingUses ?? 0)}
                  </span>
                  <span className="epd-metric__unit">건</span>
                </>
              )}
            </p>
          </div>
          <div className="epd-metric epd-metric--expiry">
            <p className="epd-metric__label">등록 만료일</p>
            <p className="epd-metric__value epd-metric__value--date">
              {coupon.expiresAt ? (
                <>
                  <span className="epd-metric__num epd-metric__num--date">{date(coupon.expiresAt)}</span>
                  <span className="epd-metric__sub">
                    {time(coupon.expiresAt)}까지 · 한국 시간
                  </span>
                </>
              ) : (
                <span className="epd-metric__num epd-metric__num--date">기한 없음</span>
              )}
            </p>
          </div>
        </div>

        <div className="epd-progress-block">
          <div className="epd-progress-meta">
            <p>
              등록률{' '}
              <strong className="epd-accent-text">{rate ? rate.label : '해당 없음'}</strong>
            </p>
            <p>
              최대 등록 수량{' '}
              <strong>{coupon.isUnlimited ? '무제한' : `${count(coupon.maxUses)}건`}</strong>
            </p>
          </div>
          {rate ? (
            <div
              className="epd-bar"
              role="progressbar"
              aria-label="최대 등록 수량 대비 누적 등록률"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Number(rate.ratio.toFixed(1))}
            >
              <span className="epd-bar__fill" style={{ width: `${rate.ratio}%` }} />
            </div>
          ) : (
            <p className="epd-progress-note">등록 수량 제한이 없어 등록률을 계산하지 않습니다.</p>
          )}
        </div>
      </section>

      <section className="epd-details" aria-labelledby="epd-details-title">
        <h2 id="epd-details-title" className="epd-section-title">
          쿠폰 정보
        </h2>
        <dl className="epd-facts">
          <div>
            <dt>쿠폰명</dt>
            <dd>{name}</dd>
          </div>
          <div>
            <dt>쿠폰 코드</dt>
            <dd className="epd-facts__code">{coupon.code}</dd>
          </div>
          <div>
            <dt>쿠폰 유형</dt>
            <dd>{coupon.type === 'subscription' ? '구독 쿠폰' : '할인 쿠폰'}</dd>
          </div>
          <div>
            <dt>{coupon.type === 'subscription' ? '제공 혜택' : '할인 값'}</dt>
            <dd>{coupon.type === 'subscription' ? couponBenefit(coupon) : count(coupon.value)}</dd>
          </div>
          <div>
            <dt>발급일</dt>
            <dd>
              {date(coupon.createdAt)} {time(coupon.createdAt)}
            </dd>
          </div>
          {description ? (
            <div className="epd-facts__full">
              <dt>설명</dt>
              <dd>{description}</dd>
            </div>
          ) : null}
        </dl>
      </section>

      <section className="epd-notes" aria-label="집계 기준">
        <p>
          등록 건수는 실제 이용 중인 인원과 다를 수 있습니다. 사용·취소·만료 상태를 포함한 전체 등록
          건수입니다.
        </p>
      </section>
    </>
  );
}

function EmptyState({
  missing,
  onRetry,
  fetching,
}: {
  missing: boolean;
  onRetry: () => void;
  fetching: boolean;
}) {
  return (
    <section className="epd-empty" role="status">
      <h2>{missing ? '아직 발급된 은평케어 쿠폰이 없습니다' : '쿠폰 현황을 불러오지 못했습니다'}</h2>
      <p>
        {missing
          ? '쿠폰이 발급되면 이곳에서 등록 현황을 확인할 수 있습니다.'
          : '서버 연결 상태를 확인한 후 다시 시도해 주세요.'}
      </p>
      <button type="button" className="epd-btn" onClick={onRetry} disabled={fetching}>
        <RefreshIcon spinning={fetching} />
        {fetching ? '확인 중' : '다시 확인'}
      </button>
    </section>
  );
}

export default function EunpyeongCareDashboardPage() {
  const { data, isPending, isFetching, isError, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['public', 'eunpyeongcare-coupon'],
    queryFn: async ({ signal }) => {
      try {
        return await fetchEunpyeongCareCoupon(signal);
      } catch (error) {
        if (error instanceof CouponDashboardError && error.kind === 'not-found') return null;
        throw error;
      }
    },
    retry: false,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  const [toast, setToast] = useState<{ id: number; message: string } | null>(null);
  const [refreshLocked, setRefreshLocked] = useState(false);
  const refreshCountRef = useRef(0);
  const cooldownUntilRef = useRef(0);

  const missing = data === null && !isError;
  const coupon = data ?? undefined;
  const status = coupon ? couponStatus(coupon) : null;

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  useEffect(() => {
    if (!refreshLocked) return;
    const remaining = Math.max(0, cooldownUntilRef.current - Date.now());
    const timer = window.setTimeout(() => {
      refreshCountRef.current = 0;
      cooldownUntilRef.current = 0;
      setRefreshLocked(false);
    }, remaining || REFRESH_COOLDOWN_MS);
    return () => window.clearTimeout(timer);
  }, [refreshLocked]);

  const showToast = (message: string) => {
    setToast((prev) => ({ id: (prev?.id ?? 0) + 1, message }));
  };

  const refresh = async () => {
    const now = Date.now();
    if (now < cooldownUntilRef.current) {
      showToast('1분 후 다시 시도해 주세요.');
      return;
    }

    refreshCountRef.current += 1;
    if (refreshCountRef.current >= REFRESH_LIMIT) {
      cooldownUntilRef.current = now + REFRESH_COOLDOWN_MS;
      setRefreshLocked(true);
      showToast('1분 후 다시 시도해 주세요.');
      return;
    }

    await refetch();
    showToast('새로고침을 완료했습니다.');
  };

  return (
    <div className="eunpyeong-dashboard epd">
      <Helmet>
        <html lang="ko" />
        <title>마음 쉼터 쿠폰 현황 | SoundPill</title>
        <meta name="description" content="은평구 담당자를 위한 은평케어 쿠폰 등록 현황 대시보드" />
        <meta name="robots" content="noindex, nofollow" />
        <meta name="theme-color" content="#ffffff" />
        <link rel="canonical" href="https://soundpill.app/eunpyeongcare/dashboard" />
      </Helmet>

      <a className="epd-skip" href="#epd-main">
        본문으로 건너뛰기
      </a>

      <header className="epd-header">
        <div className="epd-shell">
          <div className="epd-header__inner">
            <div className="epd-brand">
              <img src={soundpillLogo} alt="SoundPill" className="epd-brand__logo" />
              <span className="epd-brand__divider" aria-hidden="true" />
              <span className="epd-brand__org">{ORG_NAME}</span>
            </div>
            <span className="epd-readonly">조회 전용</span>
          </div>
        </div>
      </header>

      <div className="epd-shell epd-body">
        <div className="epd-title-row">
          <div className="epd-title-row__left">
            <h1>마음 쉼터 쿠폰 현황</h1>
            {status ? (
              <span className={`epd-status epd-status--${status.tone}`}>{status.label}</span>
            ) : null}
          </div>
          <div className="epd-title-row__right">
            <p className="epd-updated">
              {dataUpdatedAt ? (
                <>
                  마지막 업데이트{' '}
                  <time dateTime={new Date(dataUpdatedAt).toISOString()}>
                    {date(dataUpdatedAt)} {time(dataUpdatedAt)}
                  </time>
                </>
              ) : isPending ? (
                '쿠폰 현황을 확인하고 있습니다'
              ) : (
                '업데이트 대기 중'
              )}
            </p>
            <button
              type="button"
              className="epd-btn"
              onClick={() => void refresh()}
              disabled={isFetching || refreshLocked}
            >
              <RefreshIcon spinning={isFetching} />
              {isFetching ? '업데이트 중' : refreshLocked ? '잠시 후 다시' : '새로고침'}
            </button>
          </div>
        </div>

        <main id="epd-main" className="epd-main">
          {isError && coupon ? (
            <div className="epd-notice epd-notice--error" role="alert">
              <strong>최신 정보를 가져오지 못했습니다</strong>
              <p>마지막으로 확인한 현황을 표시합니다. 위 업데이트 시각을 확인해 주세요.</p>
            </div>
          ) : null}

          {isPending ? (
            <div className="epd-loading" role="status" aria-label="쿠폰 현황 불러오는 중">
              <div className="epd-skeleton epd-skeleton--panel" />
              <div className="epd-skeleton epd-skeleton--details" />
              <span className="epd-sr-only">쿠폰 현황을 불러오고 있습니다.</span>
            </div>
          ) : coupon ? (
            <CouponContent coupon={coupon} />
          ) : (
            <EmptyState missing={missing} onRetry={() => void refresh()} fetching={isFetching} />
          )}
        </main>

        <footer className="epd-footer">
          <span>© SoundPill</span>
        </footer>
      </div>

      {toast ? (
        <div key={toast.id} className="epd-toast" role="status" aria-live="polite">
          {toast.message}
        </div>
      ) : null}
    </div>
  );
}
